import { formatIDR, formatDate } from './api';

const logoKey = id => `warungpay:receipt-logo:${id}`;
const defaultReceiptLogo = '/images/LG_P4YU.png';
export function getLocalLogo(id) {
  if (!id) return '';
  try { return localStorage.getItem(logoKey(id)) || ''; } catch { return ''; }
}
export function saveLocalLogo(id, logo) {
  if (!id) throw new Error('Akun belum tersedia. Silakan masuk kembali.');
  if (logo) localStorage.setItem(logoKey(id), logo);
  else localStorage.removeItem(logoKey(id));
}

export function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const timer = setTimeout(() => reject(new Error('Logo tidak dapat dimuat. Coba unggah ulang logo.')), 10000);
    image.crossOrigin = 'anonymous';
    image.onload = () => { clearTimeout(timer); resolve(image); };
    image.onerror = () => { clearTimeout(timer); reject(new Error('Logo tidak dapat dibaca. Gunakan PNG, JPEG, atau WebP.')); };
    image.src = source;
  });
}

export async function prepareLogo(file) {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) throw new Error('Gunakan file PNG, JPEG, atau WebP.');
  if (file.size > 2 * 1024 * 1024) throw new Error('Ukuran logo maksimal 2 MB.');
  const url = URL.createObjectURL(file);
  try {
    const image = await loadImage(url);
    if (!image.width || !image.height || image.width * image.height > 20000000) throw new Error('Dimensi logo terlalu besar atau tidak valid.');
    const scale = Math.min(1, 512 / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally { URL.revokeObjectURL(url); }
}

export function receiptModel(tx, viewer) {
  const ownerId = tx.userId || tx.user?.id;
  const own = ownerId ? ownerId === viewer?.id : viewer?.role === 'mitra';
  const owner = own ? viewer : tx.user;
  const status = String(tx.status || 'PENDING').toUpperCase();
  const customerAdminFee = Number(tx.customerAdminFee ?? tx.adminFee ?? 0);
  const productPrice = Number(tx.sellPrice ?? tx.productPrice ?? tx.amount ?? 0);
  const reportedTotal = Number(tx.totalAmount ?? tx.total);
  const customerTotal = Number.isFinite(reportedTotal) && reportedTotal >= productPrice + customerAdminFee
    ? reportedTotal
    : productPrice + customerAdminFee;
  const label = { SUCCESS: 'Transaksi Berhasil', FAILED: 'Transaksi Gagal', CANCELLED: 'Transaksi Dibatalkan', REFUNDED: 'Dana Dikembalikan', PENDING: 'Transaksi Menunggu', PROCESSING: 'Transaksi Diproses' }[status] || status;
  return {
    name: owner?.mitraProfile?.shopName || owner?.warung_name || owner?.shopName || 'WARUNGPAY',
    logo: (own && getLocalLogo(viewer?.id)) || owner?.mitraProfile?.logoUrl || defaultReceiptLogo,
    status, label,
    total: formatIDR(customerTotal),
    rows: [
      ['Tanggal', formatDate(tx.createdAt || tx.created_at)],
      ['ID Transaksi', tx.id],
      ['Invoice', tx.invoiceNo],
      ['Produk', tx.product?.name || tx.product_name],
      ['Tujuan', tx.targetNumber || tx.customer_number],
      ['Biaya admin', customerAdminFee > 0 ? formatIDR(customerAdminFee) : 'Rp 0'],
      ['Serial / Token', tx.serialNumber || tx.serial_number],
      ['Keterangan', tx.failureReason || tx.failure_reason],
    ].filter(([, value]) => value != null && value !== ''),
    note: status === 'SUCCESS' ? 'Terima kasih telah bertransaksi.' : status === 'REFUNDED' ? 'Dana transaksi telah dikembalikan.' : 'Bukti status transaksi ini bukan bukti pembayaran berhasil.',
  };
}

export function wrapText(ctx, text, width) {
  const lines = [];
  for (const paragraph of String(text).split('\n')) {
    let line = '';
    for (const word of paragraph.split(/\s+/)) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width <= width) { line = candidate; continue; }
      if (line) { lines.push(line); line = ''; }
      for (const char of word) {
        if (line && ctx.measureText(line + char).width > width) { lines.push(line); line = ''; }
        line += char;
      }
    }
    lines.push(line.trim());
  }
  return lines;
}

export async function renderReceipt(model) {
  const logo = model.logo ? await loadImage(model.logo) : null;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = '500 22px Arial';
  const rows = model.rows.map(([label, value]) => ({ label, lines: wrapText(ctx, value, 390) }));
  ctx.font = 'bold 30px Arial';
  const names = wrapText(ctx, model.name, 600);
  ctx.font = '20px Arial';
  const notes = wrapText(ctx, model.note, 600);
  const header = (logo ? 140 : 32) + names.length * 38 + 175;
  const height = header + rows.reduce((sum, row) => sum + Math.max(1, row.lines.length) * 30 + 24, 0) + notes.length * 28 + 100;
  canvas.width = 1440; canvas.height = height * 2;
  ctx.scale(2, 2);
  ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, 720, height);
  ctx.fillStyle = '#00a878'; ctx.fillRect(0, 0, 720, 10);
  let y = 42;
  if (logo) {
    const scale = Math.min(180 / logo.width, 90 / logo.height);
    ctx.drawImage(logo, (720 - logo.width * scale) / 2, y, logo.width * scale, logo.height * scale);
    y += 108;
  }
  ctx.textAlign = 'center'; ctx.fillStyle = '#142c35'; ctx.font = 'bold 30px Arial';
  names.forEach(line => { ctx.fillText(line, 360, y + 28); y += 38; });
  y += 30;
  ctx.font = 'bold 24px Arial';
  ctx.fillStyle = model.status === 'SUCCESS' ? '#00865f' : ['FAILED', 'CANCELLED'].includes(model.status) ? '#be123c' : '#925b00';
  ctx.fillText(model.label, 360, y + 24); y += 60;
  ctx.font = 'bold 40px Arial'; ctx.fillStyle = '#142c35'; ctx.fillText(model.total, 360, y + 36); y += 72;
  ctx.strokeStyle = '#dce5e8'; ctx.beginPath(); ctx.moveTo(48, y); ctx.lineTo(672, y); ctx.stroke(); y += 32;
  rows.forEach(row => {
    ctx.textAlign = 'left'; ctx.font = '20px Arial'; ctx.fillStyle = '#61727a'; ctx.fillText(row.label, 48, y);
    ctx.font = '500 22px Arial'; ctx.fillStyle = '#142c35'; ctx.textAlign = 'right';
    row.lines.forEach((line, i) => ctx.fillText(line, 672, y + i * 30));
    y += Math.max(1, row.lines.length) * 30 + 24;
  });
  ctx.textAlign = 'center'; ctx.font = '20px Arial'; ctx.fillStyle = '#61727a';
  notes.forEach(line => { ctx.fillText(line, 360, y + 12); y += 28; });
  ctx.font = '16px Arial'; ctx.fillText('Diproses melalui WARUNGPAY', 360, y + 40);
  return canvas;
}

export async function receiptFiles(canvas, id) {
  const filename = `bukti-${String(id || 'transaksi').replace(/[^a-zA-Z0-9_-]/g, '_')}`;
  const png = await new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('PNG gagal dibuat.')), 'image/png'));
  const { jsPDF } = await import('jspdf');
  const width = 180, height = width * canvas.height / canvas.width;
  const pdf = new jsPDF({ unit: 'mm', format: [width, height], orientation: height >= width ? 'portrait' : 'landscape', compress: true });
  pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, width, height, undefined, 'FAST');
  return {
    png: new File([png], `${filename}.png`, { type: 'image/png' }),
    pdf: new File([pdf.output('blob')], `${filename}.pdf`, { type: 'application/pdf' }),
  };
}

export function downloadReceipt(file) {
  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = file.name;
  document.body.appendChild(anchor); anchor.click(); anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

export async function shareReceipt(file) {
  if (navigator.share && navigator.canShare?.({ files: [file] })) {
    try { await navigator.share({ files: [file], title: 'Bukti Transaksi' }); return 'shared'; }
    catch (error) { if (error.name === 'AbortError') return 'cancelled'; throw error; }
  }
  downloadReceipt(file);
  return 'downloaded';
}
